import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Checkbox } from '@/components/ui/checkbox';
import { Chip } from '@/components/Chip';
import { IoIosTrendingUp, IoIosTrendingDown } from 'react-icons/io';
import { Skeleton } from '@/components/ui/skeleton';
import { chartColors, chartColorArray } from '@/lib/colors';
import type { SpendResponse } from '@/data/types/dashboard.types';
import { format, parseISO } from 'date-fns';
import { DashboardEmptyState } from '@/components/Dashboard/EmptyState';

interface UsageChartProps {
  data?: SpendResponse;
  isLoading?: boolean;
}

// Default provider colors (will dynamically add more if needed)
const providerColors: Record<string, string> = {
  openai: chartColors.purple,
  google: chartColors.blue,
  gemini: chartColors.teal,
  anthropic: chartColors.orange,
  claude: chartColors.orange,
  other: chartColors.magenta,
};

// Get color for a provider (use predefined or assign from global chart color array)
function getProviderColor(provider: string, index: number): string {
  const normalizedProvider = provider.toLowerCase();
  return providerColors[normalizedProvider] || chartColorArray[index % chartColorArray.length];
}

// Format week label
function formatWeekLabel(dateStr: string): string {
  try {
    const date = parseISO(dateStr);
    return format(date, 'MMM d');
  } catch {
    return dateStr;
  }
}

// Custom tooltip component
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const total = payload.reduce((sum: number, entry: any) => sum + entry.value, 0);
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
        <p className="text-sm font-medium mb-2">{payload[0].payload.weekLabel}</p>
        {payload.map((entry: any, index: number) => {
          const label = entry.name || entry.dataKey;
          return (
            <p key={index} className="text-xs" style={{ color: entry.color }}>
              {label}: {entry.value.toLocaleString('en-US')}
            </p>
          );
        })}
        <p className="text-xs font-medium mt-1 pt-1 border-t border-border">
          Total: {total.toLocaleString('en-US')}
        </p>
      </div>
    );
  }
  return null;
};

// Custom X-axis tick that only shows first and last labels
const CustomXAxisTick = ({ x, y, payload, index, visibleTicksCount }: any) => {
  const isFirst = index === 0;
  const isLast = index === visibleTicksCount - 1;
  
  // Only render first and last ticks
  if (!isFirst && !isLast) return null;
  
  return (
    <text
      x={x}
      y={y + 12}
      fill="#71717a"
      fontSize={12}
      fontFamily="Jost, sans-serif"
      textAnchor={isFirst ? 'start' : 'end'}
      stroke="none"
    >
      {payload.value}
    </text>
  );
};

export const UsageChart: React.FC<UsageChartProps> = ({ data, isLoading = false }) => {
  // Extract unique providers from data
  const providers = useMemo(() => {
    if (!data?.weekly_data || data.weekly_data.length === 0) return [];
    
    const providerSet = new Set<string>();
    data.weekly_data.forEach(week => {
      week.providers.forEach(p => providerSet.add(p.provider));
    });
    
    return Array.from(providerSet).map((provider, index) => ({
      id: provider.toLowerCase().replace(/\s+/g, '_'),
      label: provider,
      color: getProviderColor(provider, index),
    }));
  }, [data]);

  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);

  // Initialize selected providers when data changes
  React.useEffect(() => {
    if (providers.length > 0 && selectedProviders.length === 0) {
      setSelectedProviders(providers.map(p => p.id));
    }
  }, [providers, selectedProviders.length]);

  // Transform data for chart
  const chartData = useMemo(() => {
    if (!data?.weekly_data) return [];
    
    return data.weekly_data.map(week => {
      const weekData: Record<string, any> = {
        weekLabel: formatWeekLabel(week.week_start_date),
      };
      
      week.providers.forEach(p => {
        const providerId = p.provider.toLowerCase().replace(/\s+/g, '_');
        weekData[providerId] = p.events;
      });
      
      return weekData;
    });
  }, [data]);

  const toggleProvider = (providerId: string) => {
    setSelectedProviders(prev => 
      prev.includes(providerId)
        ? prev.filter(id => id !== providerId)
        : [...prev, providerId]
    );
  };

  // Filter providers based on selection
  const visibleProviders = providers.filter(p => selectedProviders.includes(p.id));

  // Get stats from summary
  const totalEvents = data?.summary?.total_events ?? 0;
  const eventsTrend = data?.summary?.events_trend ?? 0;
  const isPositiveTrend = eventsTrend >= 0;

  // Check if all values are effectively zero/empty
  const hasData = useMemo(() => {
    if (!data?.weekly_data || data.weekly_data.length === 0) return false;
    return data.weekly_data.some(week => 
      week.providers.some(p => p.events > 0)
    );
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex flex-col w-full">
        {/* Metrics Section Skeleton */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
          <div className="flex flex-col gap-1">
            <Skeleton className="h-3 w-20" />
            <div className="flex items-center gap-1.5">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-[18px] w-14" />
            </div>
          </div>
        </div>

        {/* Chart Skeleton */}
        <div className="mb-6">
          <Skeleton className="h-[300px] w-full" />
        </div>

        {/* Provider Checkboxes Skeleton */}
        <div className="flex items-center justify-center gap-6 flex-wrap">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex items-center gap-2">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-3 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state when no data
  if (!hasData) {
    return <DashboardEmptyState height={400} />;
  }

  return (
    <div className="flex flex-col w-full">
      {/* Metrics Section */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
        <div className="flex flex-col gap-1">
          <span className="text-[12px] text-muted-foreground leading-none">Total Events</span>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold text-foreground leading-none whitespace-nowrap">
              {totalEvents.toLocaleString('en-US')}
            </span>
            <Chip
              label={`${isPositiveTrend ? '+' : ''}${eventsTrend.toFixed(1)}%`}
              icon={isPositiveTrend 
                ? <IoIosTrendingUp className="w-3 h-3" style={{ color: '#2ECC71' }} />
                : <IoIosTrendingDown className="w-3 h-3" style={{ color: '#E74C3C' }} />
              }
              className="h-[18px] text-[9px] px-1.5 max-w-none"
            />
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="mb-6">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={chartData}
            syncId="usage-spend-sync"
            margin={{
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              horizontal={true}
              vertical={true}
              stroke="#e5e7eb"
            />
            <XAxis 
              dataKey="weekLabel" 
              tickLine={false}
              axisLine={false}
              tick={<CustomXAxisTick visibleTicksCount={chartData.length} />}
              interval={0}
            />
            <YAxis 
              tick={{ fill: '#9ca3af', fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              domain={[0, 'auto']}
              hide={true}
            />
            <Tooltip 
              content={<CustomTooltip />} 
              cursor={{ stroke: '#6b7280', strokeWidth: 1, strokeDasharray: '5 5' }}
            />
            {visibleProviders.map(provider => (
              <Line
                key={provider.id}
                type="monotone"
                dataKey={provider.id}
                stroke={provider.color}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 6, strokeWidth: 0 }}
                name={provider.label}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Provider Checkboxes */}
      <div className="flex items-center justify-center gap-6 flex-wrap">
        {providers.map(provider => {
          const isChecked = selectedProviders.includes(provider.id);
          return (
            <label
              key={provider.id}
              className="flex items-center gap-2 cursor-pointer group"
            >
              <Checkbox
                checked={isChecked}
                onCheckedChange={() => toggleProvider(provider.id)}
                className="border-2 w-4 h-4 flex-shrink-0"
                style={{
                  borderColor: provider.color,
                  backgroundColor: isChecked ? provider.color : `${provider.color}15`,
                } as React.CSSProperties}
              />
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-foreground font-medium">
                  {provider.label}
                </span>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};
